import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function LandingPage() {
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', businessName: '', phone: '' });

  const NOTIFY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-lead`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(NOTIFY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.name,
          businessName: formData.businessName,
          phone: formData.phone,
        }),
      });

      const data = await res.json();
      console.log('notify-lead response:', data);

      if (!res.ok) {
        console.error('notify-lead failed:', res.status, data);
      }

      setFormSubmitted(true);
    } catch (err) {
      console.error('שגיאה בשליחה:', err);
      setFormSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen overflow-x-hidden"
      style={{ fontFamily: "'Assistant', sans-serif", background: '#F8F9FA', color: '#1E1B24' }}
    >
      {/* Hero Section */}
      <section className="pt-20 pb-20 px-6">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Text */}
          <div style={{ animation: 'fadeInRight 0.8s ease-out forwards' }}>
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight mb-6">
              המערכת שמנהלת את <br />
              <span
                className="underline"
                style={{ color: '#6D29FD', textDecorationColor: 'rgba(197,160,89,0.3)' }}
              >
                העסק עבורך.
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-lg leading-relaxed">
              תור בקליק, יומן חכם וקופה דיגיטלית – הכל במקום אחד. המערכת שחוסכת לך זמן ומגדילה הכנסות ב-22% בממוצע.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm text-sm font-semibold">
                <span style={{ color: '#6D29FD' }} className="font-bold">✓</span> ללא התחייבות
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm text-sm font-semibold">
                <span style={{ color: '#6D29FD' }} className="font-bold">✓</span> 500+ עסקים פעילים
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm text-sm font-semibold">
                <span style={{ color: '#6D29FD' }} className="font-bold">✓</span> תמיכה בעברית
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <button
                onClick={() => navigate('/register/business')}
                className="px-8 py-4 rounded-2xl text-white font-extrabold text-lg shadow-xl hover:opacity-90 transition-all transform hover:-translate-y-1"
                style={{ background: '#6D29FD', boxShadow: '0 8px 30px rgba(109,41,253,0.3)' }}
              >
                התחל בחינם עכשיו
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-4 rounded-2xl font-bold text-lg border-2 bg-white hover:bg-gray-50 transition-all"
                style={{ borderColor: '#6D29FD', color: '#6D29FD' }}
              >
                כניסה למערכת
              </button>
            </div>
          </div>

          {/* Right: Lead Form */}
          <div
            id="lead-form"
            className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-gray-50 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-2 h-full" style={{ background: '#6D29FD' }} />
            <h3 className="text-2xl font-extrabold mb-2">הצטרפו למהפכה</h3>
            <p className="text-gray-500 mb-8">השאירו פרטים ונחזור אליכם תוך דקות</p>

            {!formSubmitted ? (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-bold mb-2">שם מלא</label>
                  <input
                    type="text"
                    required
                    placeholder="ישראל ישראלי"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    className="w-full p-4 rounded-xl bg-gray-50 outline-none transition-all"
                    style={{ border: '2px solid transparent' }}
                    onFocus={e => e.target.style.borderColor = '#6D29FD'}
                    onBlur={e => e.target.style.borderColor = 'transparent'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2">שם העסק</label>
                  <input
                    type="text"
                    required
                    placeholder="למשל: המספרה של אביב"
                    value={formData.businessName}
                    onChange={(e) => setFormData(p => ({ ...p, businessName: e.target.value }))}
                    className="w-full p-4 rounded-xl bg-gray-50 outline-none transition-all"
                    style={{ border: '2px solid transparent' }}
                    onFocus={e => e.target.style.borderColor = '#6D29FD'}
                    onBlur={e => e.target.style.borderColor = 'transparent'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2">מספר טלפון</label>
                  <input
                    type="tel"
                    required
                    placeholder="050-0000000"
                    dir="ltr"
                    value={formData.phone}
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                    className="w-full p-4 rounded-xl bg-gray-50 outline-none transition-all text-right"
                    style={{ border: '2px solid transparent' }}
                    onFocus={e => e.target.style.borderColor = '#6D29FD'}
                    onBlur={e => e.target.style.borderColor = 'transparent'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-white font-extrabold py-5 rounded-xl transition-all transform hover:-translate-y-1 disabled:opacity-70"
                  style={{ background: '#6D29FD', boxShadow: '0 8px 24px rgba(109,41,253,0.25)' }}
                >
                  {submitting ? 'שולח...' : 'אני רוצה להתנסות בחינם'}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">🎉</div>
                <p className="text-green-600 font-bold text-lg">הפרטים התקבלו!</p>
                <p className="text-gray-500 mt-2">נציג יחזור אליך בהקדם.</p>
                <button
                  onClick={() => navigate('/register/business')}
                  className="mt-6 px-6 py-3 rounded-xl text-white font-bold transition-all hover:opacity-90"
                  style={{ background: '#6D29FD' }}
                >
                  להרשמה עכשיו
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20" style={{ background: '#1E1B24' }}>
        <div className="container mx-auto px-6 text-center mb-16">
          <h2 className="text-4xl font-extrabold mb-4 text-white">למה LOYALFLOW?</h2>
          <div className="h-1 w-20 mx-auto" style={{ background: '#6D29FD' }} />
        </div>
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: '📅', title: 'יומן תורים חכם', desc: 'הלקוחות קובעים לבד 24/7. המערכת שולחת תזכורות אוטומטיות ב-SMS כדי למנוע הברזות.' },
            { icon: '💰', title: 'קופה וחשבוניות', desc: 'סליקה מהירה מהנייד, הפקת חשבוניות ירוקות וסנכרון מלא עם רואה החשבון שלך.' },
            { icon: '📊', title: 'ניהול לקוחות CRM', desc: 'דע בדיוק מי הלקוחות הכי רווחיים שלך, שלח מבצעים ממוקדים והגדל את נאמנות הלקוחות.' },
          ].map((f) => (
            <div
              key={f.title}
              className="p-8 rounded-3xl transition-all cursor-default"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
            >
              <div className="text-4xl mb-6">{f.icon}</div>
              <h4 className="text-xl font-bold mb-4 text-white">{f.title}</h4>
              <p className="text-gray-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA Bottom */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/register/business')}
            className="px-10 py-5 rounded-2xl text-white font-extrabold text-lg shadow-xl hover:opacity-90 transition-all transform hover:-translate-y-1"
            style={{ background: '#6D29FD', boxShadow: '0 8px 30px rgba(109,41,253,0.4)' }}
          >
            התחל עכשיו – בחינם
          </button>
          <p className="text-gray-500 mt-4 text-sm">
            כבר רשום?{' '}
            <button onClick={() => navigate('/login')} className="text-[#6D29FD] font-bold hover:underline">
              כניסה למערכת
            </button>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-gray-200 text-center" style={{ background: '#F8F9FA' }}>
        <div className="text-xl font-extrabold mb-3" style={{ color: '#6D29FD' }}>LOYALFLOW</div>
        <p className="text-gray-500 text-sm">כל הזכויות שמורות © 2026 | הופכים כל תור לחוויה יוקרתית</p>
      </footer>
    </div>
  );
}

export default LandingPage;
